import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { Button, Input } from '@agms/ui'
import { StatementDashboard } from '../components/statement/StatementDashboard'
import { StatementPagePicker } from '../components/statement/StatementPagePicker'
import { PageHeader, SurfaceCard } from '../components/ui'
import { apiErrorMessage, apiValidationErrors } from '../hooks/useErrorHandler'
import { usePermissions } from '../hooks/useAuth'
import api from '../lib/api'
import { formatCurrencyWithCentsOrDash } from '../lib/format'
import { categoryTotalWarning } from '../lib/statementSavings'

interface ScheduleSummary {
  id: string
  name: string
  version_number: number
  status: string
  effective_on: string
  auth_buy_per_item: string
  monthly_cost: string
  assessment_visa_percent: string
  assessment_mastercard_percent: string
  assessment_discover_percent: string
  assessment_amex_percent: string
}

interface Allocation {
  brand?: string
  category_code?: string
  category_name?: string
  volume: string
  transaction_count: string
  rate_percent: string
  per_item_fee: string | null
  fee_structure: string
  minimum_per_txn?: string | null
  cap_per_txn?: string | null
  industry?: string | null
  ticket_min?: string | null
  ticket_max?: string | null
  ticket_min_inclusive?: boolean | null
  ticket_max_inclusive?: boolean | null
  verified?: boolean | null
  source_type?: string | null
  direction?: string | null
}

interface AnalysisResult {
  current_fees_total?: string | null
  current_effective_rate_percent?: string | null
  proposed_merchant_cost?: string | null
  proposed_effective_rate_percent?: string | null
  cardholder_program_fee?: string | null
  merchant_savings?: string | null
  interchange_cost?: string | null
  schedule_a_cost?: string | null
  agms_margin?: string | null
  agms_margin_percent_of_volume?: string | null
  uses_unverified_rates?: boolean
  line_items?: Array<{
    category_name?: string | null
    category_code?: string | null
    direction?: string
    unpriced?: boolean
    per_item_unknown?: boolean
    verified?: boolean | null
    amount?: string | null
  }>
  warnings?: string[]
  assumptions?: string[]
}

interface LeadMatch {
  id: string
  dba_name: string | null
  legal_name: string | null
  stage: string
}

interface FeeCategoryLine {
  description: string | null
  sales_amount: string | null
  item_count: number | null
  discount_rate: string | null
  per_item_rate: string | null
  fee_amount: string | null
  confidence: number | null
  page: number | null
}

interface FeeCategory {
  name: string
  amount: string | null
  confidence: number | null
  page: number | null
  lines: FeeCategoryLine[]
}

interface Extraction {
  status?: string | null
  low_confidence_fields?: string[]
  confidence_threshold?: string | number | null
  truncated?: boolean
  category_sum_warning?: string | null
  fields?: Record<string, { value?: string | number | null; confidence?: number | null; page?: number | null }>
  fee_lines?: Array<{
    bucket: string
    amount: string | null
    confidence: number | null
    label: string | null
    page?: number | null
  }>
  fee_categories?: FeeCategory[]
}

interface Analysis {
  id: string
  merchant_label: string
  lead_id?: string | null
  lead?: { id: string; dba_name: string | null; legal_name: string | null; stage: string } | null
  processor_name?: string | null
  statement_period: string | null
  entry_mode?: 'quick' | 'detailed' | string | null
  schedule_a_version_id: string | null
  pricing_model: string
  gross_volume: string
  transaction_count: number
  total_fees?: string | null
  visa_volume: string | null
  mastercard_volume: string | null
  discover_volume: string | null
  amex_volume: string | null
  amex_count?: number | null
  pin_debit_volume?: string | null
  pin_debit_count?: number | null
  visa_count?: number | null
  mastercard_count?: number | null
  discover_count?: number | null
  visa_discount_percent?: string | null
  visa_per_item_fee?: string | null
  mastercard_discount_percent?: string | null
  mastercard_per_item_fee?: string | null
  discover_discount_percent?: string | null
  discover_per_item_fee?: string | null
  amex_discount_percent?: string | null
  amex_per_item_fee?: string | null
  pin_debit_discount_percent?: string | null
  pin_debit_per_item_fee?: string | null
  fixed_monthly_fees?: string | null
  fixed_monthly_fees_label?: string | null
  current_discount_rate_percent?: string | null
  current_per_item_fee?: string | null
  current_auth_fee?: string | null
  discount_fees: string
  per_item_fees: string
  monthly_fees: string
  other_fees: string
  interchange_cost_override: string | null
  proposed_rates: Record<string, string>
  category_allocations: Allocation[]
  reconciliation_warnings?: string[]
  result: AnalysisResult | null
  ocr_status?: string
  reader_configured?: boolean
  extraction?: Extraction | null
  file: { id?: string; file_name: string; mime_type: string; file_size: number; position?: number } | null
  files?: Array<{ id?: string; file_name: string; mime_type: string; file_size: number; position?: number }>
  schedule_a_version: ScheduleSummary | null
}

type FigureKey =
  | 'gross_volume'
  | 'transaction_count'
  | 'visa_volume'
  | 'mastercard_volume'
  | 'discover_volume'
  | 'amex_volume'
  | 'pin_debit_volume'
  | 'discount_fees'
  | 'per_item_fees'
  | 'monthly_fees'
  | 'other_fees'

const FIGURES: Array<{ key: FigureKey; label: string; numeric?: boolean }> = [
  { key: 'gross_volume', label: 'Gross volume' },
  { key: 'transaction_count', label: 'Transaction count', numeric: true },
  { key: 'visa_volume', label: 'Visa volume' },
  { key: 'mastercard_volume', label: 'Mastercard volume' },
  { key: 'discover_volume', label: 'Discover volume' },
  { key: 'amex_volume', label: 'Amex volume' },
  { key: 'pin_debit_volume', label: 'PIN debit volume' },
  { key: 'discount_fees', label: 'Discount fees' },
  { key: 'per_item_fees', label: 'Per-item fees' },
  { key: 'monthly_fees', label: 'Monthly fees' },
  { key: 'other_fees', label: 'Other fees' },
]

const CARD_ROWS = [
  { key: 'visa', label: 'Visa' },
  { key: 'mastercard', label: 'Mastercard' },
  { key: 'discover', label: 'Discover' },
  { key: 'amex', label: 'Amex' },
  { key: 'pin_debit', label: 'PIN debit' },
] as const

type CardKey = (typeof CARD_ROWS)[number]['key']
type CardPart = 'volume' | 'count' | 'discount_percent' | 'per_item_fee'

function cardFieldName(key: CardKey, part: CardPart): keyof Analysis {
  if (part === 'volume') return `${key}_volume`
  if (part === 'count') return `${key}_count`
  if (part === 'discount_percent') return `${key}_discount_percent`
  return `${key}_per_item_fee`
}

const FEE_BUCKETS: Record<string, string> = {
  discount_fees: 'Discount fees',
  per_item_fees: 'Per-item fees',
  monthly_fees: 'Monthly fees',
  other_fees: 'Other fees',
}

interface CatalogRow {
  brand: string
  category_code: string
  category_name: string
  rate_percent: string
  per_item_fee: string | null
  fee_structure: string
  minimum_per_txn: string | null
  cap_per_txn: string | null
  industry?: string | null
  ticket_min?: string | null
  ticket_max?: string | null
  ticket_min_inclusive?: boolean | null
  ticket_max_inclusive?: boolean | null
  verified?: boolean
  source_type?: string | null
  direction?: string | null
}

function catalogOptionLabel(row: CatalogRow): string {
  const missingItem = row.per_item_fee == null || row.per_item_fee === ''
  const item = missingItem
    ? (row.brand === 'amex_optblue' ? 'per-item unknown' : 'no per-item fee')
    : row.per_item_fee
  const bounds = [
    row.ticket_min == null ? '' : `${row.ticket_min_inclusive === false ? '>' : '>='}${row.ticket_min}`,
    row.ticket_max == null ? '' : `${row.ticket_max_inclusive === false ? '<' : '<='}${row.ticket_max}`,
  ].filter(Boolean).join(' ')
  const ticket = bounds === '' ? '' : `, ticket ${bounds}`
  const unverified = row.verified === false ? ', unverified' : ''
  return `${row.category_name} (${row.rate_percent}% + ${item}${ticket}${unverified})`
}

const MODEL_FIELDS: Record<string, Array<{ key: string; label: string }>> = {
  interchange_plus: [
    { key: 'markup_percent', label: 'Markup percent' },
    { key: 'per_item_fee', label: 'Per-item fee' },
    { key: 'monthly_fee', label: 'Monthly fee' },
  ],
  flat: [
    { key: 'flat_rate_percent', label: 'Flat rate percent' },
    { key: 'per_item_fee', label: 'Per-item fee' },
    { key: 'monthly_fee', label: 'Monthly fee' },
  ],
  tiered: [
    { key: 'qualified_rate_percent', label: 'Qualified rate percent' },
    { key: 'qualified_volume_percent', label: 'Qualified volume percent' },
    { key: 'mid_qualified_rate_percent', label: 'Mid-qualified rate percent' },
    { key: 'mid_qualified_volume_percent', label: 'Mid-qualified volume percent' },
    { key: 'non_qualified_rate_percent', label: 'Non-qualified rate percent' },
    { key: 'non_qualified_volume_percent', label: 'Non-qualified volume percent' },
    { key: 'per_item_fee', label: 'Per-item fee' },
    { key: 'monthly_fee', label: 'Monthly fee' },
  ],
  cash_discount: [
    { key: 'program_rate_percent', label: 'Program percent' },
    { key: 'per_item_fee', label: 'Per-item fee' },
    { key: 'monthly_fee', label: 'Monthly fee' },
  ],
}

function text(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function reviewNote(low: boolean, page: number | null | undefined, base?: string): string | undefined {
  const lead = low ? 'Low confidence. Review this figure before confirming.' : (base ?? '')
  const pageNote = typeof page === 'number' ? `Source page ${page}.` : ''
  const notes = [lead, pageNote].filter((part) => part !== '')
  return notes.length > 0 ? notes.join(' ') : undefined
}

const PROPOSED_FIGURES = [
  'gross_volume',
  'total_fees',
  'visa_volume',
  'mastercard_volume',
  'discover_volume',
  'amex_volume',
  'pin_debit_volume',
  'visa_discount_percent',
  'visa_per_item_fee',
  'mastercard_discount_percent',
  'mastercard_per_item_fee',
  'discover_discount_percent',
  'discover_per_item_fee',
  'amex_discount_percent',
  'amex_per_item_fee',
  'pin_debit_discount_percent',
  'pin_debit_per_item_fee',
  'fixed_monthly_fees',
  'current_discount_rate_percent',
  'current_per_item_fee',
  'current_auth_fee',
  'discount_fees',
  'per_item_fees',
  'monthly_fees',
  'other_fees',
] as const

const PROPOSED_COUNTS = [
  'visa_count',
  'mastercard_count',
  'discover_count',
  'amex_count',
  'pin_debit_count',
] as const

function figureSaveError(caught: unknown): string {
  const errors = apiValidationErrors(caught)
  const first = errors
    ? Object.values(errors).find((messages) => messages.length > 0)?.[0]
    : undefined
  if (first) return first
  return apiErrorMessage(caught, 'The analysis could not be saved.')
}

function effectiveRate(fees: string | null | undefined, volume: string | null | undefined): string | null {
  const fee = Number(fees)
  const vol = Number(volume)
  if (!vol || Number.isNaN(fee)) return null
  return ((fee / vol) * 100).toFixed(4)
}

function reviewFigures(analysis: Analysis): Analysis {
  const fields = analysis.ocr_status === 'proposed' ? analysis.extraction?.fields : undefined
  if (!fields) return analysis

  const next: Analysis = { ...analysis }
  for (const key of PROPOSED_FIGURES) {
    const value = fields[key]?.value
    if (value === null || value === undefined || value === '') continue
    next[key] = String(value)
  }
  const count = fields.transaction_count?.value
  if (count !== null && count !== undefined && count !== '') {
    next.transaction_count = Number(count)
  }
  for (const key of PROPOSED_COUNTS) {
    const value = fields[key]?.value
    if (value === null || value === undefined || value === '') continue
    next[key] = Number(value)
  }
  const processor = fields.processor_name?.value
  if (typeof processor === 'string' && processor !== '') next.processor_name = processor
  const period = fields.statement_period?.value
  if (typeof period === 'string' && period !== '') next.statement_period = period
  const fixedLabel = fields.fixed_monthly_fees_label?.value
  if (typeof fixedLabel === 'string' && fixedLabel !== '') next.fixed_monthly_fees_label = fixedLabel
  const interchange = fields.interchange_cost_override?.value
  if (interchange !== null && interchange !== undefined && interchange !== '') {
    next.interchange_cost_override = String(interchange)
  }

  return next
}

function applyAnalysis(current: Analysis | null, next: Analysis): Analysis {
  const reviewed = reviewFigures(next)
  return {
    ...(current ?? reviewed),
    ...reviewed,
    proposed_rates: reviewed.proposed_rates ?? {},
    category_allocations: reviewed.category_allocations ?? [],
  }
}

export function StatementAnalysisDetailPage() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()
  const permissions = usePermissions()
  const canCreateLead = permissions?.canCreateLeads?.() ?? false
  const [form, setForm] = useState<Analysis | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ocrMessage, setOcrMessage] = useState<string | null>(null)
  const [catalogCode, setCatalogCode] = useState('')
  const [duplicates, setDuplicates] = useState<LeadMatch[] | null>(null)
  const [hiddenMatches, setHiddenMatches] = useState(0)
  const [showEditor, setShowEditor] = useState(false)

  const analysis = useQuery({
    queryKey: ['statement-analysis', id],
    queryFn: async () => (await api.get<Analysis>(`/statement-analyses/${id}`)).data,
    enabled: id !== '',
  })
  const schedules = useQuery({
    queryKey: ['schedule-a'],
    queryFn: async () => (await api.get<{ data: ScheduleSummary[] }>('/schedule-a')).data.data,
  })
  const catalog = useQuery({
    queryKey: ['interchange-categories'],
    queryFn: async () => (await api.get<{ data: CatalogRow[] }>('/interchange-categories')).data.data,
  })

  useEffect(() => {
    if (analysis.data) {
      setForm(applyAnalysis(null, analysis.data))
    }
  }, [analysis.data])

  const payload = (entryMode?: 'quick' | 'detailed') => {
    if (!form) return {}
    const blank = (value: string | number | null | undefined) => {
      if (value === null || value === undefined || value === '') return null
      return value
    }
    return {
      merchant_label: form.merchant_label,
      processor_name: blank(form.processor_name),
      statement_period: blank(form.statement_period),
      entry_mode: entryMode ?? (form.entry_mode === 'detailed' ? 'detailed' : 'quick'),
      schedule_a_version_id: form.schedule_a_version_id,
      pricing_model: form.pricing_model,
      gross_volume: form.gross_volume,
      transaction_count: Number(form.transaction_count || 0),
      total_fees: blank(form.total_fees),
      ...Object.fromEntries(CARD_ROWS.flatMap((row) => (
        (['volume', 'count', 'discount_percent', 'per_item_fee'] as const).map((part) => {
          const name = cardFieldName(row.key, part)
          return [name, blank(form[name] as string | number | null | undefined)]
        })
      ))),
      fixed_monthly_fees: blank(form.fixed_monthly_fees),
      fixed_monthly_fees_label: blank(form.fixed_monthly_fees_label),
      current_discount_rate_percent: blank(form.current_discount_rate_percent),
      current_per_item_fee: blank(form.current_per_item_fee),
      current_auth_fee: blank(form.current_auth_fee),
      discount_fees: form.discount_fees,
      per_item_fees: form.per_item_fees,
      monthly_fees: form.monthly_fees,
      other_fees: form.other_fees,
      interchange_cost_override: form.interchange_cost_override || null,
      proposed_rates: form.proposed_rates,
      category_allocations: form.category_allocations.map((row) => ({
        ...row,
        transaction_count: Number(row.transaction_count || 0),
      })),
    }
  }

  const save = useMutation({
    mutationFn: async (path: 'update' | 'analyze' | 'detailed') => {
      const url = path === 'analyze'
        ? `/statement-analyses/${id}/analyze`
        : `/statement-analyses/${id}`
      const body = payload(path === 'detailed' ? 'detailed' : undefined)
      const response = path === 'analyze'
        ? await api.post<Analysis>(url, body)
        : await api.patch<Analysis>(url, body)
      return response.data
    },
    onSuccess: (updated) => {
      setForm(updated)
      setError(null)
      queryClient.setQueryData(['statement-analysis', id], updated)
    },
    onError: (caught) => setError(figureSaveError(caught)),
  })

  const upload = useMutation({
    mutationFn: async ({ files, onProgress }: { files: File[]; onProgress: (percent: number) => void }) => {
      const body = new FormData()
      files.forEach((file) => body.append('files[]', file))
      const response = await api.post<Analysis>(`/statement-analyses/${id}/file`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          const total = event.total ?? 0
          if (total > 0) onProgress(Math.min(100, Math.round((event.loaded * 100) / total)))
        },
      })
      return response.data
    },
    onSuccess: (updated) => {
      setForm(updated)
      queryClient.setQueryData(['statement-analysis', id], updated)
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'The statement could not be uploaded.')),
  })

  const removeFile = useMutation({
    mutationFn: async () => {
      const response = await api.delete<Analysis>(`/statement-analyses/${id}/file`)
      return response.data
    },
    onSuccess: (updated) => {
      setForm(updated)
      queryClient.setQueryData(['statement-analysis', id], updated)
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'The statement could not be deleted.')),
  })

  const createLead = useMutation({
    mutationFn: async (body: { confirm_create?: boolean; link_lead_id?: string }) => (
      await api.post<Analysis | { lead_id?: string; analysis?: Analysis }>(
        `/statement-analyses/${id}/lead`,
        body,
      )
    ).data,
    onSuccess: (result) => {
      setDuplicates(null)
      setHiddenMatches(0)
      const analysis = 'merchant_label' in result ? result : result.analysis
      if (analysis) {
        setForm((current) => current ? { ...current, ...analysis, category_allocations: analysis.category_allocations ?? current.category_allocations } : analysis)
        queryClient.setQueryData(['statement-analysis', id], analysis)
      }
      queryClient.invalidateQueries({ queryKey: ['statement-analysis', id] })
      queryClient.invalidateQueries({ queryKey: ['statement-analyses'] })
    },
    onError: (caught) => {
      if (axios.isAxiosError(caught) && caught.response?.status === 409) {
        const body = caught.response.data as { matches?: LeadMatch[]; hidden_match_count?: number }
        setDuplicates(body.matches ?? [])
        setHiddenMatches(body.hidden_match_count ?? 0)
        setError(null)
        return
      }
      setError(apiErrorMessage(caught, 'The lead could not be created.'))
    },
  })

  const parse = useMutation({
    mutationFn: async () => (await api.post<{ message: string; analysis?: Analysis }>(`/statement-analyses/${id}/parse`)).data,
    onSuccess: (result) => {
      setOcrMessage(result.message)
      setError(null)
      if (result.analysis) {
        setForm((current) => applyAnalysis(current, result.analysis as Analysis))
        queryClient.setQueryData(['statement-analysis', id], result.analysis)
      }
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'The statement could not be read.')),
  })

  const confirmExtraction = useMutation({
    mutationFn: async () => (await api.post<Analysis>(`/statement-analyses/${id}/confirm-extraction`, payload())).data,
    onSuccess: (updated) => {
      setForm(applyAnalysis(null, updated))
      setError(null)
      setOcrMessage('Extracted figures confirmed. You can run the analysis.')
      queryClient.setQueryData(['statement-analysis', id], updated)
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'The extracted figures could not be confirmed.')),
  })

  const selected = schedules.data?.find((version) => version.id === form?.schedule_a_version_id) ?? form?.schedule_a_version ?? null
  const result = form?.result
  const statementPages = form?.files?.length ? form.files : form?.file ? [form.file] : []
  const reviewPending = form?.ocr_status === 'proposed'
  const categoryWarning = form
    ? categoryTotalWarning(form.extraction?.fee_categories ?? [], form.total_fees, form.extraction?.category_sum_warning)
    : null
  const lowConfidence = new Set(reviewPending ? form?.extraction?.low_confidence_fields ?? [] : [])
  const confidenceThreshold = Number(form?.extraction?.confidence_threshold ?? 0.8)

  return (
    <div className="space-y-6">
      <PageHeader
        title={form?.merchant_label || 'Statement analysis'}
        subtitle="What they pay today, and what they would pay with AGMS."
        action={<Link to="/statement-analysis" className="text-sm text-sky-700">All analyses</Link>}
      />

      {error && !showEditor ? <p className="text-sm text-red-700" role="alert">{error}</p> : null}
      {analysis.isLoading || !form ? <p className="text-sm text-gray-500">Loading analysis…</p> : null}

      {form ? (
        <>
          <SurfaceCard>
            <h2 className="text-sm font-semibold text-gray-900">Lead</h2>
            {form.lead ? (
              <p className="mt-2 text-sm text-gray-700">
                Linked to{' '}
                <Link to={`/leads/${form.lead.id}`} className="font-medium text-sky-700 hover:text-sky-800">
                  {form.lead.dba_name || form.lead.legal_name || 'Unnamed lead'}
                </Link>
              </p>
            ) : form.lead_id ? (
              <p className="mt-2 text-sm text-gray-700">This analysis is linked to a lead you cannot open.</p>
            ) : (
              <div className="mt-2 space-y-3">
                <p className="text-sm text-gray-700">This analysis is not linked to a lead.</p>
                {canCreateLead ? (
                  <Button type="button" onClick={() => createLead.mutate({})} disabled={createLead.isPending}>
                    Create lead from merchant name
                  </Button>
                ) : null}
                {duplicates ? (
                  <div className="space-y-2" role="alert">
                    <p className="text-sm text-gray-700">A lead with this merchant name already exists.</p>
                    {hiddenMatches > 0 ? (
                      <p className="text-sm text-gray-600">
                        {hiddenMatches} matching {hiddenMatches === 1 ? 'lead is' : 'leads are'} on another book.
                      </p>
                    ) : null}
                    <ul className="space-y-2">
                      {duplicates.map((match) => (
                        <li key={match.id} className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm text-gray-800">{match.dba_name || match.legal_name || 'Unnamed lead'}</span>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => createLead.mutate({ link_lead_id: match.id })}
                            disabled={createLead.isPending}
                          >
                            Link this lead
                          </Button>
                        </li>
                      ))}
                    </ul>
                    <Button type="button" variant="secondary" onClick={() => createLead.mutate({ confirm_create: true })} disabled={createLead.isPending}>
                      Create a separate lead
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </SurfaceCard>

          <SurfaceCard>
            <StatementPagePicker
              storedPages={statementPages}
              readerOff={form.reader_configured === false}
              readPending={parse.isPending}
              deletePending={removeFile.isPending}
              statusMessage={ocrMessage}
              onUpload={async (files, onProgress) => {
                await upload.mutateAsync({ files, onProgress })
              }}
              onRead={() => parse.mutate()}
              onDeleteStored={() => removeFile.mutate()}
            />
          </SurfaceCard>

          {showEditor ? (
          <>
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => setShowEditor(false)}>Back to summary</Button>
          </div>
          <SurfaceCard>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {form.entry_mode === 'detailed' ? 'Detailed statement figures' : 'Quick statement figures'}
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  {form.entry_mode === 'detailed'
                    ? 'Detailed entry keeps the card rows. Fee totals that do not match the statement total show a warning.'
                    : 'Enter the summary totals used to price a proposal. Line-by-line entry stays available.'}
                </p>
              </div>
              {form.entry_mode === 'detailed' ? null : (
                <Button type="button" variant="outline" onClick={() => save.mutate('detailed')} disabled={save.isPending}>
                  Switch to detailed entry
                </Button>
              )}
            </div>
            {reviewPending && form.extraction == null ? (
              <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
                The statement file changed. Read it again, or confirm the figures on this form before running the analysis.
              </p>
            ) : null}
            {reviewPending && form.extraction != null ? (
              <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="status">
                These figures were read from the statement. Review them, especially the highlighted fields, then confirm before running the analysis.
              </p>
            ) : null}
            {reviewPending && form.extraction?.truncated ? (
              <p className="mt-3 text-sm text-amber-800" role="status">Only part of the statement was read. Review every figure.</p>
            ) : null}
            {categoryWarning ? (
              <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
                {categoryWarning}
              </p>
            ) : null}
            {form.entry_mode === 'detailed' && (form.total_fees || form.visa_volume || form.pin_debit_volume) ? (
              <dl className="mt-3 grid gap-2 rounded-md border border-gray-200 p-3 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Quick total fees</dt>
                  <dd>{formatCurrencyWithCentsOrDash(form.total_fees)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Quick Visa volume</dt>
                  <dd>{formatCurrencyWithCentsOrDash(form.visa_volume)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Quick PIN debit volume</dt>
                  <dd>{formatCurrencyWithCentsOrDash(form.pin_debit_volume)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Quick Amex volume</dt>
                  <dd>{formatCurrencyWithCentsOrDash(form.amex_volume)}</dd>
                </div>
              </dl>
            ) : null}
            {form.reconciliation_warnings && form.reconciliation_warnings.length > 0 ? (
              <ul className="mt-3 list-disc rounded-md border border-amber-300 bg-amber-50 p-3 pl-8 text-sm text-amber-950" role="alert">
                {form.reconciliation_warnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            ) : null}
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Input label="Merchant label" value={form.merchant_label} onChange={(event) => setForm({ ...form, merchant_label: event.target.value })} />
              <Input label="Processor name" value={text(form.processor_name)} onChange={(event) => setForm({ ...form, processor_name: event.target.value })} />
              <Input
                label={form.entry_mode === 'detailed' ? 'Statement period' : 'Statement month'}
                value={text(form.statement_period)}
                onChange={(event) => setForm({ ...form, statement_period: event.target.value })}
              />
              {form.entry_mode === 'detailed' ? null : (
                <>
                  <Input label="Total sales volume" value={text(form.gross_volume)} onChange={(event) => setForm({ ...form, gross_volume: event.target.value })} />
                  <Input label="Transaction count" value={text(form.transaction_count)} onChange={(event) => setForm({ ...form, transaction_count: Number(event.target.value || 0) })} />
                  <div className={lowConfidence.has('total_fees') ? 'rounded-md bg-amber-50 p-2 ring-2 ring-amber-400' : undefined}>
                    <Input
                      label="Total fees"
                      value={text(form.total_fees)}
                      required
                      helperText={[
                        lowConfidence.has('total_fees')
                          ? 'Low confidence. Review this figure before confirming.'
                          : `Required. Effective rate ${effectiveRate(form.total_fees, form.gross_volume) ?? '—'}%. Current cost is the total fees charged on the statement.`,
                        reviewPending && typeof form.extraction?.fields?.total_fees?.page === 'number'
                          ? `Source page ${form.extraction.fields.total_fees.page}.`
                          : '',
                      ].filter(Boolean).join(' ')}
                      onChange={(event) => setForm({ ...form, total_fees: event.target.value })}
                    />
                  </div>
                  <Input label="Fixed monthly fees" value={text(form.fixed_monthly_fees)} helperText="Monthly, statement, equipment, and funding fees as one number." onChange={(event) => setForm({ ...form, fixed_monthly_fees: event.target.value })} />
                  <Input label="Fixed monthly fees label" value={text(form.fixed_monthly_fees_label)} onChange={(event) => setForm({ ...form, fixed_monthly_fees_label: event.target.value })} />
                  <Input label="Auth fee" value={text(form.current_auth_fee)} helperText="The auth fee printed on the statement." onChange={(event) => setForm({ ...form, current_auth_fee: event.target.value })} />
                  <div className="md:col-span-2 overflow-x-auto">
                    <table className="w-full min-w-[40rem] text-sm">
                      <caption className="mb-2 text-left text-sm font-medium text-gray-900">Card types on the statement</caption>
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="py-1 pr-2 font-medium">Card type</th>
                          <th className="py-1 pr-2 font-medium">Volume</th>
                          <th className="py-1 pr-2 font-medium">Count</th>
                          <th className="py-1 pr-2 font-medium">Discount %</th>
                          <th className="py-1 font-medium">Per item</th>
                        </tr>
                      </thead>
                      <tbody>
                        {CARD_ROWS.map((row) => (
                          <tr key={row.key}>
                            <th scope="row" className="py-1 pr-2 text-left font-medium text-gray-800">{row.label}</th>
                            {(['volume', 'count', 'discount_percent', 'per_item_fee'] as const).map((part) => {
                              const name = cardFieldName(row.key, part)
                              const partLabel = part === 'volume'
                                ? 'volume'
                                : part === 'count'
                                  ? 'count'
                                  : part === 'discount_percent'
                                    ? 'discount percent'
                                    : 'per-item fee'
                              return (
                                <td key={part} className="py-1 pr-2">
                                  <input
                                    aria-label={`${row.label} ${partLabel}`}
                                    className={lowConfidence.has(name)
                                      ? 'w-full rounded-md border border-amber-400 bg-amber-50 px-2 py-1'
                                      : 'w-full rounded-md border border-gray-300 px-2 py-1'}
                                    value={text(form[name] as string | number | null | undefined)}
                                    onChange={(event) => setForm({ ...form, [name]: event.target.value })}
                                  />
                                  {reviewPending && typeof form.extraction?.fields?.[name]?.page === 'number' ? (
                                    <span className="mt-1 block text-xs text-gray-600">Source page {form.extraction.fields[name].page}.</span>
                                  ) : null}
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {form.entry_mode === 'detailed' ? FIGURES.map((field) => (
                <div
                  key={field.key}
                  className={lowConfidence.has(field.key) ? 'rounded-md bg-amber-50 p-2 ring-2 ring-amber-400' : undefined}
                >
                  <Input
                    label={field.label}
                    value={text(form[field.key])}
                    helperText={reviewPending
                      ? reviewNote(lowConfidence.has(field.key), form.extraction?.fields?.[field.key]?.page)
                      : undefined}
                    onChange={(event) => setForm({
                      ...form,
                      [field.key]: field.numeric ? Number(event.target.value || 0) : event.target.value,
                    })}
                  />
                </div>
              )) : null}
              {form.entry_mode === 'detailed' ? (
              <div className={lowConfidence.has('interchange_cost_override') ? 'rounded-md bg-amber-50 p-2 ring-2 ring-amber-400' : undefined}>
                <Input
                  label="Interchange dollars on the statement"
                  value={text(form.interchange_cost_override)}
                  onChange={(event) => setForm({ ...form, interchange_cost_override: event.target.value })}
                  helperText={reviewPending
                    ? reviewNote(
                        lowConfidence.has('interchange_cost_override'),
                        form.extraction?.fields?.interchange_cost_override?.page,
                        'Used only when no category allocations are entered.',
                      )
                    : 'Used only when no category allocations are entered.'}
                />
              </div>
              ) : null}
            </div>
            {form.extraction?.fee_lines && form.extraction.fee_lines.length > 0 ? (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-900">Fee lines from the statement</h3>
                <ul className="mt-2 space-y-2">
                  {form.extraction.fee_lines.map((line, index) => {
                    const lineLow = reviewPending && (line.confidence ?? 0) < confidenceThreshold
                    return (
                      <li
                        key={`${line.bucket}-${index}`}
                        className={lineLow ? 'rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-950 ring-2 ring-amber-400' : 'rounded-md px-3 py-2 text-sm text-gray-700'}
                      >
                        {(line.label || FEE_BUCKETS[line.bucket] || 'Fee')}: {formatCurrencyWithCentsOrDash(line.amount)}
                        {typeof line.page === 'number' ? `, source page ${line.page}` : ''}
                        {lineLow ? ' — low confidence' : ''}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}
            {form.extraction?.fee_categories && form.extraction.fee_categories.length > 0 ? (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-900">Fee categories from the statement</h3>
                <ul className="mt-2 space-y-3">
                  {form.extraction.fee_categories.map((category, index) => {
                    const categoryLow = reviewPending && (category.confidence ?? 0) < confidenceThreshold
                    return (
                      <li
                        key={`${category.name}-${index}`}
                        className={categoryLow ? 'rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-950 ring-2 ring-amber-400' : 'rounded-md px-3 py-2 text-sm text-gray-700'}
                      >
                        <p>
                          {category.name}: {formatCurrencyWithCentsOrDash(category.amount)}
                          {typeof category.page === 'number' ? `, source page ${category.page}` : ''}
                          {categoryLow ? ' — low confidence' : ''}
                        </p>
                        {category.lines.length > 0 ? (
                          <ul className="mt-2 space-y-1 pl-4">
                            {category.lines.map((line, lineIndex) => {
                              const lineLow = reviewPending && (line.confidence ?? 0) < confidenceThreshold
                              return (
                                <li key={`${category.name}-${lineIndex}`} className={lineLow ? 'text-amber-950' : undefined}>
                                  {line.description || 'Fee'}
                                  {line.sales_amount ? `, sales ${formatCurrencyWithCentsOrDash(line.sales_amount)}` : ''}
                                  {line.item_count != null ? `, count ${line.item_count}` : ''}
                                  {line.discount_rate ? `, discount ${line.discount_rate}` : ''}
                                  {line.per_item_rate ? `, per item ${line.per_item_rate}` : ''}
                                  {line.fee_amount ? `, fee ${formatCurrencyWithCentsOrDash(line.fee_amount)}` : ''}
                                  {typeof line.page === 'number' ? `, source page ${line.page}` : ''}
                                  {lineLow ? ' — low confidence' : ''}
                                </li>
                              )
                            })}
                          </ul>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}
          </SurfaceCard>

          <SurfaceCard>
            <h2 className="text-sm font-semibold text-gray-900">Schedule A</h2>
            <p className="mt-1 text-xs text-gray-500">
              Choose which published version this analysis uses. Buy rates are read-only here.
            </p>
            <label htmlFor="schedule-a" className="mt-4 block text-sm font-medium text-gray-700">Schedule A</label>
            <select
              id="schedule-a"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={form.schedule_a_version_id ?? ''}
              onChange={(event) => setForm({ ...form, schedule_a_version_id: event.target.value || null })}
            >
              <option value="">No Schedule A selected</option>
              {schedules.data?.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.name} · v{version.version_number} · {version.status}
                </option>
              ))}
            </select>
            {selected ? (
              <dl className="mt-4 grid gap-2 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Auth buy per item</dt>
                  <dd>{selected.auth_buy_per_item}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Monthly cost</dt>
                  <dd>{formatCurrencyWithCentsOrDash(selected.monthly_cost)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Visa assessment percent</dt>
                  <dd>{selected.assessment_visa_percent}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Mastercard assessment percent</dt>
                  <dd>{selected.assessment_mastercard_percent}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Discover assessment percent</dt>
                  <dd>{selected.assessment_discover_percent}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Amex assessment percent</dt>
                  <dd>{selected.assessment_amex_percent}</dd>
                </div>
              </dl>
            ) : (
              <p className="mt-3 text-sm text-gray-500">No Schedule A version is selected, so margin cannot be calculated.</p>
            )}
          </SurfaceCard>

          <SurfaceCard>
            <h2 className="text-sm font-semibold text-gray-900">Proposed pricing</h2>
            <label htmlFor="pricing-model" className="mt-4 block text-sm font-medium text-gray-700">Pricing model</label>
            <select
              id="pricing-model"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={form.pricing_model}
              onChange={(event) => setForm({ ...form, pricing_model: event.target.value })}
            >
              <option value="interchange_plus">Interchange plus</option>
              <option value="flat">Flat</option>
              <option value="tiered">Tiered</option>
              <option value="cash_discount">Cash discount</option>
            </select>
            {form.entry_mode !== 'detailed' && (form.pricing_model === 'flat' || form.pricing_model === 'cash_discount') ? (
              <p className="mt-3 text-xs text-gray-600">
                The per-item fee applies to every card transaction, including credit, debit, and Amex. It is not an authorization fee.
              </p>
            ) : null}
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {(MODEL_FIELDS[form.pricing_model] ?? []).map((field) => (
                <Input
                  key={field.key}
                  label={field.label}
                  value={text(form.proposed_rates[field.key])}
                  onChange={(event) => setForm({
                    ...form,
                    proposed_rates: { ...form.proposed_rates, [field.key]: event.target.value },
                  })}
                />
              ))}
            </div>
          </SurfaceCard>

          {form.entry_mode === 'detailed' ? (
          <SurfaceCard>
            <h2 className="text-sm font-semibold text-gray-900">Interchange allocations</h2>
            <p className="mt-1 text-xs text-gray-500">
              Optional. Allocations replace the statement interchange dollars. Rates come from the published table.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label htmlFor="catalog-row" className="text-sm text-gray-700">
                Published category
                <select
                  id="catalog-row"
                  className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={catalogCode}
                  onChange={(event) => setCatalogCode(event.target.value)}
                >
                  <option value="">Select a category</option>
                  {catalog.data?.map((row) => (
                    <option key={`${row.brand}-${row.category_code}`} value={row.category_code}>
                      {catalogOptionLabel(row)}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const row = catalog.data?.find((item) => item.category_code === catalogCode)
                  if (!row) return
                  setForm({
                    ...form,
                    category_allocations: [
                      ...form.category_allocations,
                      {
                        brand: row.brand,
                        category_code: row.category_code,
                        category_name: row.category_name,
                        volume: '0',
                        transaction_count: '0',
                        rate_percent: row.rate_percent,
                        per_item_fee: row.per_item_fee,
                        fee_structure: row.fee_structure,
                        minimum_per_txn: row.minimum_per_txn,
                        cap_per_txn: row.cap_per_txn,
                        industry: row.industry ?? null,
                        ticket_min: row.ticket_min ?? null,
                        ticket_max: row.ticket_max ?? null,
                        ticket_min_inclusive: row.ticket_min_inclusive ?? null,
                        ticket_max_inclusive: row.ticket_max_inclusive ?? null,
                        verified: row.verified ?? null,
                        source_type: row.source_type ?? null,
                        direction: row.direction ?? null,
                      },
                    ],
                  })
                }}
              >
                Add category
              </Button>
            </div>
            <ul className="mt-4 space-y-3">
              {form.category_allocations.map((row, index) => (
                <li key={`${row.category_code}-${index}`} className="grid gap-3 rounded-md border border-gray-200 p-3 md:grid-cols-3">
                  <p className="text-sm font-medium text-gray-800 md:col-span-3">{row.category_name || row.category_code}</p>
                  <Input
                    label="Volume"
                    value={row.volume}
                    onChange={(event) => {
                      const next = [...form.category_allocations]
                      next[index] = { ...row, volume: event.target.value }
                      setForm({ ...form, category_allocations: next })
                    }}
                  />
                  <Input
                    label="Transactions"
                    value={row.transaction_count}
                    onChange={(event) => {
                      const next = [...form.category_allocations]
                      next[index] = { ...row, transaction_count: event.target.value }
                      setForm({ ...form, category_allocations: next })
                    }}
                  />
                  {row.brand === 'amex_optblue' ? (
                    <div>
                      {row.per_item_fee == null || row.per_item_fee === '' ? (
                        <p className="text-sm text-amber-800">Per-item fee is unknown.</p>
                      ) : null}
                      <Input
                        label="OptBlue per-item fee"
                        value={row.per_item_fee ?? ''}
                        placeholder="unknown"
                        onChange={(event) => {
                          const next = [...form.category_allocations]
                          next[index] = { ...row, per_item_fee: event.target.value === '' ? null : event.target.value }
                          setForm({ ...form, category_allocations: next })
                        }}
                      />
                    </div>
                  ) : null}
                  {row.verified === false ? (
                    <p className="text-sm text-amber-800 md:col-span-3">This rate is unverified.</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </SurfaceCard>
          ) : null}

          {error ? <p className="text-sm text-red-700" role="alert">{error}</p> : null}
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={() => save.mutate('update')} disabled={save.isPending}>
              Save figures
            </Button>
            {reviewPending ? (
              <Button type="button" onClick={() => confirmExtraction.mutate()} disabled={confirmExtraction.isPending}>
                Confirm extracted figures
              </Button>
            ) : null}
            <Button type="button" onClick={() => save.mutate('analyze')} disabled={save.isPending || reviewPending}>
              Run analysis
            </Button>
          </div>

          {result ? (
            <SurfaceCard>
              <h2 className="text-sm font-semibold text-gray-900">Result</h2>
              {result.uses_unverified_rates ? (
                <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
                  This analysis uses an unverified interchange rate. Confirm it before relying on these totals.
                </p>
              ) : null}
              {result.line_items?.some((line) => line.unpriced || line.per_item_unknown) ? (
                <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
                  Interchange cost is incomplete. An unpriced line or an unknown OptBlue per-item fee was not costed at $0.
                </p>
              ) : null}
              <dl className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                <div>
                  <dt>Interchange cost</dt>
                  <dd>{formatCurrencyWithCentsOrDash(result.interchange_cost)}</dd>
                </div>
                <div>
                  <dt>Current fees</dt>
                  <dd>{formatCurrencyWithCentsOrDash(result.current_fees_total)}</dd>
                </div>
                <div>
                  <dt>Current effective rate</dt>
                  <dd>{result.current_effective_rate_percent ? `${result.current_effective_rate_percent}%` : '-'}</dd>
                </div>
                <div>
                  <dt>Proposed merchant cost</dt>
                  <dd>{formatCurrencyWithCentsOrDash(result.proposed_merchant_cost)}</dd>
                </div>
                <div>
                  <dt>Merchant savings</dt>
                  <dd>{formatCurrencyWithCentsOrDash(result.merchant_savings)}</dd>
                </div>
                <div>
                  <dt>Schedule A cost</dt>
                  <dd>{formatCurrencyWithCentsOrDash(result.schedule_a_cost)}</dd>
                </div>
                <div>
                  <dt>AGMS margin</dt>
                  <dd>{formatCurrencyWithCentsOrDash(result.agms_margin)}</dd>
                </div>
              </dl>
              {result.warnings && result.warnings.length > 0 ? (
                <ul className="mt-4 list-disc pl-5 text-sm text-amber-800">
                  {result.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                </ul>
              ) : null}
              {result.assumptions && result.assumptions.length > 0 ? (
                <ul className="mt-4 list-disc pl-5 text-sm text-gray-600">
                  {result.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}
                </ul>
              ) : null}
            </SurfaceCard>
          ) : null}
          </>
          ) : (
            <StatementDashboard
              analysis={form}
              reviewPending={reviewPending}
              lowConfidenceFields={reviewPending ? form.extraction?.low_confidence_fields ?? [] : []}
              onConfirm={() => confirmExtraction.mutate()}
              onAnalyze={() => save.mutate('analyze')}
              confirmPending={confirmExtraction.isPending}
              analyzePending={save.isPending}
              onEditFigures={() => setShowEditor(true)}
            />
          )}
        </>
      ) : null}
    </div>
  )
}
